import { parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum } from 'nuqs'
import { createSearchParamsCache } from 'nuqs/server'
import { constructVerticalFilter } from '@utils/filters'
import { GetUserTokensQueryVariables, Order_By, Tokens_Metadata_Order_By } from '@/gql/generated/graphql'
import { constructFullTagFilter } from '@utils/filters'

export enum ValidUserTokensSort {
  recently_collected = 'Recently collected',
  first_collected = 'First collected',
  oldest_collection = 'Oldest collection',
  newest_collection = 'Newest collection'
}
export const userTokensFilterParsers = {
  sort: parseAsStringEnum(Object.values(ValidUserTokensSort)).withDefault(ValidUserTokensSort.recently_collected),
  page: parseAsInteger.withDefault(1),
  tags: parseAsArrayOf(parseAsString).withDefault([]),
  category: parseAsArrayOf(parseAsString).withDefault([]),
  search: parseAsArrayOf(parseAsString).withDefault([])
}

export const userTokensFilterCache = createSearchParamsCache(userTokensFilterParsers)
export const NUM_USER_TOKENS_PER_PAGE = 100

export const constructUserTokenGridOrderBy = (sortOrder: ValidUserTokensSort): Tokens_Metadata_Order_By[] => {
  switch (sortOrder) {
    case ValidUserTokensSort.recently_collected:
      return [{ last_transferred_at: Order_By.DescNullsLast }]
    case ValidUserTokensSort.first_collected:
      return [{ last_transferred_at: Order_By.AscNullsLast }]
    case ValidUserTokensSort.oldest_collection:
      return [{ minted_at: Order_By.AscNullsLast }]
    case ValidUserTokensSort.newest_collection:
      return [{ minted_at: Order_By.DescNullsLast }]
  }
}

export const constructUserTokenGridDocumentVariables = ({
  sortOrder,
  pageNumber,
  tagsFilterValue,
  categoryFilterValue,
  walletAddresses,
  searchTerms,
  limit
}: {
  sortOrder: ValidUserTokensSort
  pageNumber: number
  tagsFilterValue: Array<string>
  categoryFilterValue: Array<string>
  walletAddresses: Array<string>
  searchTerms?: Array<string>
  limit?: number
}): GetUserTokensQueryVariables => {
  const itemsPerPage = limit ?? NUM_USER_TOKENS_PER_PAGE

  // Build search filters for project name, artist name, and token number with OR logic
  const searchFilters = []

  if (searchTerms && searchTerms.length > 0) {
    const orConditions = []

    for (const searchTerm of searchTerms) {
      const trimmedSearch = searchTerm.trim()

      if (!trimmedSearch) continue

      // Check if search term is a number (for token search)
      const isNumericSearch = /^\d+$/.test(trimmedSearch)

      if (isNumericSearch) {
        // Search by token number (invocation)
        orConditions.push({
          invocation: { _eq: parseInt(trimmedSearch) }
        })
      } else {
        // Search by project name or artist name
        orConditions.push({
          _or: [
            {
              project: {
                name: { _ilike: `%${trimmedSearch}%` }
              }
            },
            {
              project: {
                artist_name: { _ilike: `%${trimmedSearch}%` }
              }
            }
          ]
        })
      }
    }

    if (orConditions.length > 0) {
      searchFilters.push({
        _or: orConditions
      })
    }
  }

  return {
    offset: (pageNumber - 1) * itemsPerPage,
    limit: itemsPerPage,
    orderBy: constructUserTokenGridOrderBy(sortOrder),
    where: {
      _and: [
        ...(walletAddresses.length ? [{ owner_address: { _in: walletAddresses } }] : []),
        ...searchFilters,
        ...(tagsFilterValue.length || categoryFilterValue.length
          ? [
              {
                project: {
                  _and: [
                    ...(tagsFilterValue.length ? [constructFullTagFilter(tagsFilterValue)] : []),
                    ...(categoryFilterValue.length ? [constructVerticalFilter(categoryFilterValue)] : [])
                  ]
                }
              }
            ]
          : [])
      ]
    }
  }
}
